import { TFunction } from 'i18next';
import React, { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { DefaultTheme, useTheme } from 'styled-components';

import { GeodataStatus } from '../../../../typings/api/skymap/rest/v0/.common';
import { WebsocketMessage } from '../../../../typings/websocket-messages';
import { formattedDateTime } from '../../utils/dateUtils';
import { isDefined } from '../../utils/variables';
import { WebsocketManager } from '../../utils/websocket/websocket-manager';
import { SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { Hyperlink, HyperlinkStyled } from '../components/hyperlink/hyperlink';
import { Icon } from '../components/icon/icon';
import { cadDrawingReportExport } from '../components/skyview-new/components/report-tool-menu/cad-drawing-pdf-report/report-export';
import { useSubscribeCallback } from '../hooks/use-subscribe';
import { useOptionalAuthState } from './auth-state';

export type NotificationBellStatus =
  | 'connecting'
  | 'disconnected'
  | 'connected'
  | 'messageRecieved';

export type NotificationItem = {
  id: string;
  timestamp: string;
  icon: ReactNode;
  content: ReactNode;
  onClick?: () => void | Promise<void>;
};

type WebsocketStateContextValue = {
  status: NotificationBellStatus;
  notifications: NotificationItem[];
  notificationsOpen: boolean;
  setNotificationsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  acknowledgeMessages: () => void;
  clearNotification: (id: string) => void;
  clearNotifications: () => void;
  connect: () => void;
  subscribeToMessages: (listener: (message: WebsocketMessage) => void) => () => void;
};

const WebsocketStateContext = React.createContext<WebsocketStateContextValue | undefined>(
  undefined,
);

function shouldAutoOpenNotificationCenter(data: WebsocketMessage) {
  if (data.message === 'BatchDownloadUpdated' && data.progress === 0) {
    return true;
  }

  if (data.message === 'FileVersionExportUpdated' && data.status === 'RUNNING') {
    return true;
  }

  return false;
}

function getFilteredNotifications(
  oldItems: NotificationItem[],
  data: WebsocketMessage,
  theme: DefaultTheme,
  t: TFunction,
  navigate: ReturnType<typeof useNavigate>,
): NotificationItem[] {
  const concatenateItems = (newItem: NotificationItem) => [
    newItem,
    ...oldItems.filter((x) => x.id !== newItem.id),
  ];

  if (data.message === 'FileVersionExportUpdated') {
    if (data.status === 'COMPLETED' && data.resultSaved) {
      return concatenateItems({
        id: data.fileName,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon
            color={theme.color.green}
            fixedWidth={true}
            icon={['fas', 'check-circle']}
            size={'xl'}
          />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{
                hyperlink: <Hyperlink target="_blank" url={data.downloadUrl} />,
              }}
              i18nKey="notifications.fileReadyAndAvailable"
              ns="components"
              values={{
                fileName: data.fileName,
              }}
            />
          </NotificationContentLabel>
        ),
      });
    }

    if (data.status === 'FAILED') {
      return concatenateItems({
        id: data.fileName,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon
            color={theme.color.red}
            fixedWidth={true}
            icon={['fad', 'exclamation-circle']}
            size={'xl'}
          />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{
                bold: <strong />,
              }}
              i18nKey="notifications.fileError"
              ns="components"
              values={{
                fileName: data.fileName,
              }}
            />
          </NotificationContentLabel>
        ),
      });
    }

    if (data.status === 'RUNNING') {
      return concatenateItems({
        id: data.fileName,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon
            color={theme.color.gray.medium}
            fixedWidth={true}
            icon={['fas', 'spinner']}
            size={'xl'}
            spin={true}
          />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{
                bold: <strong />,
              }}
              i18nKey="notifications.fileProcessing"
              ns="components"
              values={{
                fileName: data.fileName,
                progress: 0,
              }}
            />
          </NotificationContentLabel>
        ),
      });
    }
  }

  if (data.message === 'SkyviewReportUpdated') {
    // Reports exported from another tab have no name here.
    const fileName =
      cadDrawingReportExport.getFileName(data.exportId) ??
      t('reportTypeSection.cadDrawing', { ns: 'skyviewReport' });

    if (data.status === 'COMPLETED') {
      return concatenateItems({
        id: data.exportId,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon
            color={theme.color.green}
            fixedWidth={true}
            icon={['fas', 'check-circle']}
            size={'xl'}
          />
        ),
        content: (
          <NotificationContentLabel>
            {data.resultSaved ? (
              <Trans
                components={{
                  hyperlink: <Hyperlink target="_blank" url={data.downloadUrl} />,
                }}
                i18nKey="notifications.fileReadyAndAvailable"
                ns="components"
                values={{ fileName }}
              />
            ) : (
              <Trans
                components={{
                  hyperlink: <Hyperlink target="_blank" url={data.downloadUrl} />,
                }}
                i18nKey="notifications.fileReady"
                ns="components"
                values={{ fileName }}
              />
            )}
          </NotificationContentLabel>
        ),
      });
    }

    if (data.status === 'FAILED') {
      return concatenateItems({
        id: data.exportId,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon
            color={theme.color.red}
            fixedWidth={true}
            icon={['fad', 'exclamation-circle']}
            size={'xl'}
          />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{ bold: <strong /> }}
              i18nKey="notifications.fileError"
              ns="components"
              values={{ fileName }}
            />
          </NotificationContentLabel>
        ),
      });
    }

    return concatenateItems({
      id: data.exportId,
      timestamp: formattedDateTime(Date.now()),
      icon: (
        <Icon
          color={theme.color.gray.medium}
          fixedWidth={true}
          icon={['fas', 'spinner']}
          size={'xl'}
          spin={true}
        />
      ),
      content: (
        <NotificationContentLabel>
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="notifications.fileProcessing"
            ns="components"
            values={{ fileName, progress: Math.round(data.progress * 100) }}
          />
        </NotificationContentLabel>
      ),
    });
  }

  if (data.message === 'GeodataUpdated' && isDefined(data.projectName)) {
    if (data.jobStatus === 'FAILED' && data.gcpCorrectionRequested !== true) {
      return concatenateItems({
        id: data.geodataId,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon
            color={theme.color.red}
            fixedWidth={true}
            icon={['fad', 'exclamation-circle']}
            size={'xl'}
          />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{
                hyperlink: (
                  <HyperlinkStyled
                    onClick={(event) => {
                      event.preventDefault();
                      void navigate(`/projects/${data.projectId}/geodata/${data.geodataId}/viewer`);
                    }}
                  />
                ),
                bold: <strong />,
              }}
              i18nKey="notifications.processFailed"
              ns="cloudProcessing"
              values={{
                geodataName: data.geodataName,
                projectName: data.projectName,
              }}
            />
          </NotificationContentLabel>
        ),
      });
    }

    if (
      (data.geodataStatus as GeodataStatus) === 'user_move_flags' &&
      data.state === 'IDLE' &&
      data.gcpCorrectionRequested !== true
    ) {
      return concatenateItems({
        id: data.geodataId,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon color={theme.color.blue} fixedWidth={true} icon={['fad', 'flag']} size={'xl'} />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{
                hyperlink: (
                  <HyperlinkStyled
                    onClick={(event) => {
                      event.preventDefault();
                      void navigate(`/projects/${data.projectId}/geodata/${data.geodataId}/viewer`);
                    }}
                  />
                ),
                bold: <strong />,
              }}
              i18nKey="notifications.processReadyForGcpAdjustment"
              ns="cloudProcessing"
              values={{
                geodataName: data.geodataName,
                projectName: data.projectName,
              }}
            />
          </NotificationContentLabel>
        ),
      });
    }

    if (isDefined(data.projectName) && data.state === 'FINISHED') {
      return concatenateItems({
        id: data.geodataId,
        timestamp: formattedDateTime(Date.now()),
        icon: (
          <Icon color={theme.color.green} fixedWidth={true} icon={['fad', 'drone']} size={'xl'} />
        ),
        content: (
          <NotificationContentLabel>
            <Trans
              components={{
                hyperlink: (
                  <HyperlinkStyled
                    onClick={(event) => {
                      event.preventDefault();
                      void navigate(`/projects/${data.projectId}/viewer`);
                    }}
                  />
                ),
                bold: <strong />,
              }}
              i18nKey="notifications.processFinished"
              ns="cloudProcessing"
              values={{
                geodataName: data.geodataName,
                projectName: data.projectName,
              }}
            />
          </NotificationContentLabel>
        ),
      });
    }
  }

  if (data.message === 'BatchDownloadCompleted') {
    return concatenateItems({
      id: data.batchDownloadId,
      timestamp: formattedDateTime(Date.now()),
      icon: (
        <Icon
          color={theme.color.green}
          fixedWidth={true}
          icon={['fas', 'check-circle']}
          size={'xl'}
        />
      ),
      content: (
        <NotificationContentLabel>
          <Trans
            components={{
              hyperlink: <Hyperlink target="_blank" url={data.downloadUrl} />,
            }}
            i18nKey="notifications.fileReady"
            ns="components"
            values={{
              fileName: data.fileName,
            }}
          />
        </NotificationContentLabel>
      ),
    });
  }

  if (data.message === 'BatchDownloadUpdated') {
    const hasFailed = data.status === 'FAILED';
    return concatenateItems({
      id: data.batchDownloadId,
      timestamp: formattedDateTime(Date.now()),
      icon: (
        <Icon
          color={hasFailed ? theme.color.red : theme.color.gray.medium}
          fixedWidth={true}
          icon={hasFailed ? ['fad', 'exclamation-circle'] : ['fas', 'spinner']}
          size={'xl'}
          spin={!hasFailed}
        />
      ),
      content: hasFailed ? (
        <NotificationContentLabel>
          <Trans
            components={{
              bold: <strong />,
            }}
            i18nKey="notifications.fileError"
            ns="components"
            values={{
              fileName: data.fileName,
            }}
          />
        </NotificationContentLabel>
      ) : (
        <NotificationContentLabel>
          <Trans
            components={{
              bold: <strong />,
            }}
            i18nKey="notifications.fileProcessing"
            ns="components"
            values={{
              fileName: data.fileName,
              progress: (data.progress * 100).toFixed(0),
            }}
          />
        </NotificationContentLabel>
      ),
    });
  }

  return oldItems;
}

export const WebsocketStateProvider = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authState = useOptionalAuthState();
  const [status, setStatus] = React.useState<NotificationBellStatus>(
    WebsocketManager.instance.status,
  );
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);

  const listeners = React.useRef(new Set<(message: WebsocketMessage) => void>());

  const subscribeToMessages = React.useCallback((listener: (message: WebsocketMessage) => void) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);

  const notifyListeners = React.useCallback((message: WebsocketMessage) => {
    listeners.current.forEach((listener) => listener(message));
  }, []);

  const handleMessage = React.useCallback(
    (data: WebsocketMessage) => {
      setStatus('messageRecieved');
      setNotifications((oldItems) => getFilteredNotifications(oldItems, data, theme, t, navigate));
      notifyListeners(data);
      if (shouldAutoOpenNotificationCenter(data)) {
        setNotificationsOpen(true);
      }
    },
    [navigate, notifyListeners, t, theme],
  );

  useSubscribeCallback(SubscriptionTopic.WebsocketStatusChanged, (message) => {
    setStatus(message.status);
  });

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (data) => {
    handleMessage(data);
  });

  // Only connect websocket when user is authenticated.
  // Skip connection on the access-token-redirect route — the websocket server
  // may not have propagated the freshly issued token yet. The websocket will
  // connect after navigation to the target page.
  const location = useLocation();
  const isAccessTokenRedirect = location.pathname.startsWith('/redirectaccess');

  React.useEffect(() => {
    const isAuthenticated = authState?.status === 'authenticated';

    if (isAuthenticated && !isAccessTokenRedirect) {
      // Connect if authenticated and currently disconnected
      if (WebsocketManager.instance.status === 'disconnected') {
        WebsocketManager.instance.connect();
      }
    } else {
      // Disconnect if user is not authenticated (or auth state is not yet loaded)
      if (WebsocketManager.instance.status !== 'disconnected') {
        WebsocketManager.instance.disconnect();
        setStatus('disconnected');
      }
    }
  }, [authState?.status, isAccessTokenRedirect]);

  const clearNotification = React.useCallback((id: string) => {
    setNotifications((old) => old.filter((x) => x.id !== id));
  }, []);

  const clearNotifications = React.useCallback(() => setNotifications([]), []);

  const acknowledgeMessages = React.useCallback(() => {
    setStatus(WebsocketManager.instance.status);
  }, []);

  const connect = React.useCallback(() => {
    WebsocketManager.instance.connect();
  }, []);

  const value: WebsocketStateContextValue = React.useMemo(
    () => ({
      status,
      notifications,
      notificationsOpen,
      setNotificationsOpen,
      acknowledgeMessages,
      clearNotification,
      clearNotifications,
      connect,
      subscribeToMessages,
    }),
    [
      acknowledgeMessages,
      clearNotification,
      clearNotifications,
      connect,
      notifications,
      notificationsOpen,
      status,
      subscribeToMessages,
    ],
  );

  return <WebsocketStateContext.Provider value={value}>{children}</WebsocketStateContext.Provider>;
};

export const useWebsocketState = () => {
  const context = React.useContext(WebsocketStateContext);
  if (!context) {
    throw new Error('useWebsocketState must be used within WebsocketStateProvider');
  }
  return context;
};

const NotificationContentLabel = styled.label`
  font-size: 0.8em;
`;
